import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function TrialPage() {
  const location = useLocation();

  useEffect(() => {
    document.title = '廿日市の塾 無料体験授業｜学習塾NICK｜廿日市市中学生の体験・学力診断・個別相談';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '廿日市の塾・学習塾NICKの無料体験授業ページ。廿日市市の中学生向けに体験授業・学力診断・個別相談をすべて無料で実施。廿日市市大野で少人数指導・個別指導の授業を体感できます。高校受験対策や定期テスト対策のご相談もお気軽に。');
    setMeta('keywords', '廿日市 塾 無料体験,廿日市 中学生 塾 体験授業,廿日市 学習塾 体験,廿日市 塾 学力診断,廿日市 個別相談,学習塾NICK 無料体験,廿日市市大野 塾 体験');
    setMeta('og:title', '廿日市の塾 無料体験授業｜学習塾NICK', true);
    setMeta('og:description', '廿日市市の中学生向け学習塾NICKの無料体験授業。体験授業・学力診断・個別相談をすべて無料で実施。廿日市市大野で授業の雰囲気を体感できます。', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/trial`, true);
    setMeta('twitter:title', '廿日市の塾 無料体験授業｜学習塾NICK');
    setMeta('twitter:description', '廿日市市の中学生向け学習塾NICKの無料体験授業。体験授業・学力診断・個別相談をすべて無料で実施。');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/trial`);

    // Schema.org JSON-LD
    const schemaId = 'schema-trial-page';
    let schemaEl = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = schemaId;
      schemaEl.type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "廿日市の塾 無料体験授業｜学習塾NICK",
      "description": "廿日市市の中学生向け学習塾NICKの無料体験授業。体験授業・学力診断・個別相談をすべて無料で実施。",
      "url": `${import.meta.env.VITE_SITE_URL}/trial`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "ホーム", "item": `${import.meta.env.VITE_SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "無料体験授業", "item": `${import.meta.env.VITE_SITE_URL}/trial` }
        ]
      },
      "mainEntity": {
        "@type": "Service",
        "name": "無料体験授業",
        "description": "廿日市市の中学生向けに体験授業・学力診断・個別相談をすべて無料で実施。廿日市市大野で少人数指導・個別指導の授業を体感できます。",
        "provider": {
          "@type": "Organization",
          "name": "学習塾NICK",
          "url": `${import.meta.env.VITE_SITE_URL}/`
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "JPY",
          "description": "無料体験授業・学力診断・個別相談すべて無料"
        },
        "areaServed": ["廿日市市", "廿日市市大野", "阿品", "大竹市", "宮島"],
        "serviceType": "学習塾 無料体験授業"
      },
      "potentialAction": {
        "@type": "ReserveAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL}/trial#trial-form-section`,
          "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
        },
        "result": {
          "@type": "Reservation",
          "name": "無料体験授業の予約"
        }
      }
    });

    return () => {
      document.title = '廿日市の塾なら学習塾NICK｜廿日市市の中学生塾・成績アップ・高校受験対策';
      if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/`);
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    if (location.hash === '#trial-form-section') {
      setTimeout(() => {
        const el = document.getElementById('trial-form-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const [formData, setFormData] = useState({
    studentName: '',
    grade: '',
    parentName: '',
    phone: '',
    email: '',
    preferredDate: '',
    preferredTime: '',
    course: '',
    subjects: [] as string[],
    concerns: '',
    howFound: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('studentName', formData.studentName);
      formBody.append('grade', formData.grade);
      formBody.append('parentName', formData.parentName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('preferredDate', formData.preferredDate);
      formBody.append('preferredTime', formData.preferredTime);
      formBody.append('course', formData.course);
      formBody.append('subjects', formData.subjects.join(', '));
      formBody.append('concerns', formData.concerns);
      formBody.append('howFound', formData.howFound);

      const response = await fetch('https://readdy.ai/api/form/d6bgsaaohb161tfd7hn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          studentName: '',
          grade: '',
          parentName: '',
          phone: '',
          email: '',
          preferredDate: '',
          preferredTime: '',
          course: '',
          subjects: [],
          concerns: '',
          howFound: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'ri-book-open-line',
      title: '実際の授業を体験',
      description: '通常授業と同じ内容で、学習塾NICKの指導方法を体感できます',
    },
    {
      icon: 'ri-user-heart-line',
      title: '個別面談を実施',
      description: '保護者様とお子様の学習状況や目標をじっくりヒアリングします',
    },
    {
      icon: 'ri-file-list-3-line',
      title: '最適な学習プランをご提案',
      description: 'お子様に合わせた具体的な学習計画と目標達成までのロードマップをご提示します',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: '高校受験・定期テスト対策のご相談',
      description: '志望校選びから定期テスト対策まで、受験のプロが具体的なアドバイスをお伝えします',
    },
  ];

  const flow = [
    {
      number: '01',
      title: 'お申し込み',
      description: 'フォームまたはお電話でお申し込みください',
    },
    {
      number: '02',
      title: '日程調整',
      description: 'ご都合の良い日時を調整させていただきます',
    },
    {
      number: '03',
      title: '体験授業',
      description: '実際の授業を体験していただきます（60分程度）',
    },
    {
      number: '04',
      title: '個別面談',
      description: '学習状況のヒアリングと最適なプランをご提案します',
    },
    {
      number: '05',
      title: 'ご入塾',
      description: 'ご納得いただけましたらご入塾手続きを行います',
    },
  ];

  const faqs = [
    {
      question: '体験授業は何回受けられますか？',
      answer: '無料体験授業は1回受けていただけます。少人数指導または個別指導のいずれかをお選びいただけます。'
    },
    {
      question: '保護者の同席は必要ですか？',
      answer: '体験授業は生徒様のみでも構いませんが、その後の面談には保護者様のご同席をお願いしております。'
    },
    {
      question: '持ち物は何が必要ですか？',
      answer: '筆記用具とノートをお持ちください。学校の教科書やワークがあればお持ちいただくと、より具体的なアドバイスができます。'
    },
    {
      question: '体験後、必ず入塾しなければいけませんか？',
      answer: 'いいえ、体験後にご検討いただき、納得された上で入塾をお決めください。無理な勧誘は一切いたしません。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-[#4a90e2]/10 via-white to-[#4a90e2]/5">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-block bg-[#4a90e2] text-white px-6 py-2 rounded-full mb-6">
              <p className="text-sm font-semibold">無料体験受付中</p>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1a2332] mb-6">
              無料体験授業
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              <strong>廿日市市大野</strong>の学習塾NICKの授業を無料で体験できます。<br />
              まずはお気軽にお申し込みください。
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              無料体験でできること
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              学習塾NICKの無料体験では、以下のサービスをすべて無料でご利用いただけます
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                    <i className={`${benefit.icon} text-3xl text-[#4a90e2]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Flow Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              体験の流れ
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              お申し込みから体験授業までの流れをご説明します
            </p>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {flow.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                    <div className="text-5xl font-bold text-[#4a90e2]/20 mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-bold text-[#1a2332] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {index < flow.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-3xl text-[#4a90e2]"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section id="trial-form-section" className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              無料体験申込フォーム
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto leading-relaxed">
              以下のフォームに必要事項をご記入の上、送信してください。<br />
              2営業日以内にご連絡いたします。
            </p>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 text-green-700">
                  <i className="ri-checkbox-circle-line text-3xl"></i>
                  <div>
                    <p className="font-bold text-lg">お申し込みありがとうございます</p>
                    <p className="text-sm">2営業日以内にご連絡いたします。</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-500 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 text-red-700">
                  <i className="ri-error-warning-line text-3xl"></i>
                  <div>
                    <p className="font-bold text-lg">送信に失敗しました</p>
                    <p className="text-sm">お手数ですが、お電話でお問い合わせください。</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-[#f8f9fa] rounded-3xl p-10" data-readdy-form id="trial-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    生徒氏名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    学年 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="中学1年生">中学1年生</option>
                    <option value="中学2年生">中学2年生</option>
                    <option value="中学3年生">中学3年生</option>
                    <option value="小学生">小学生</option>
                    <option value="高校生">高校生</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    保護者氏名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    placeholder="山田 花子"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                      希望日
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                      希望時間帯
                    </label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                    >
                      <option value="">選択してください</option>
                      <option value="14:00-16:00">14:00-16:00</option>
                      <option value="16:00-18:00">16:00-18:00</option>
                      <option value="18:00-20:00">18:00-20:00</option>
                      <option value="20:00-22:00">20:00-22:00</option>
                      <option value="土曜午前">土曜午前</option>
                      <option value="土曜午後">土曜午後</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    希望コース
                  </label>
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="スタディコース">スタディコース</option>
                    <option value="数学強化コース">数学強化コース</option>
                    <option value="完全個別指導コース">完全個別指導コース</option>
                    <option value="サポートコース">サポートコース</option>
                    <option value="きまっていない">きまっていない</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    体験したい科目（複数選択可）
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['数学', '英語', '国語', '理科', '社会'].map((subject) => (
                      <label
                        key={subject}
                        className="flex items-center gap-2 bg-white px-4 py-3 rounded-xl border-2 border-gray-200 hover:border-[#4a90e2] cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.subjects.includes(subject)}
                          onChange={() => handleSubjectChange(subject)}
                          className="w-4 h-4 text-[#4a90e2] cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    学習に関するお悩み・ご相談
                  </label>
                  <textarea
                    name="concerns"
                    value={formData.concerns}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm resize-none"
                    placeholder="例：定期テストの点数が伸びない、苦手科目を克服したい、高校受験に向けて何をすればいいかわからない など"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">
                    {formData.concerns.length}/500文字
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1a2332] mb-2">
                    当塾を知ったきっかけ
                  </label>
                  <select
                    name="howFound"
                    value={formData.howFound}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#4a90e2] focus:outline-none text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="インターネット検索">インターネット検索</option>
                    <option value="友人・知人の紹介">友人・知人の紹介</option>
                    <option value="チラシ・ポスター">チラシ・ポスター</option>
                    <option value="通りがかり">通りがかり</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#e8734a] text-white py-5 rounded-full text-lg font-bold hover:bg-[#d4623b] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      送信
                      <i className="ri-send-plane-fill text-xl"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-600 text-center leading-relaxed">
                  ご入力いただいた個人情報は、お問い合わせへの対応および当塾からのご案内のみに使用し、<br />
                  第三者に提供することはございません。
                </p>
              </div>
            </form>

            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-6">
                お急ぎの方は、お電話またはLINEでもお申し込みいただけます
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:0829-78-1638"
                  className="bg-[#1a2332] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#2a3442] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  0829-78-1638
                </a>
                <a
                  href="https://lin.ee/Od9tigSF"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#06c755] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#05b34a] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-line-fill text-xl"></i>
                  LINEで申し込む
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              よくあるご質問
            </h2>
            <p className="text-gray-600 text-center mb-16">
              無料体験についてのよくある質問
            </p>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8"
                >
                  <h3 className="text-lg font-bold text-[#1a2332] mb-4 flex items-start gap-3">
                    <i className="ri-question-line text-2xl text-[#4a90e2] flex-shrink-0"></i>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed pl-11">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 bg-[#1a2332]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              まずは無料体験からお気軽にご相談ください
            </h2>
            <p className="text-white/80 text-lg mb-10 leading-relaxed">
              <strong>廿日市市大野</strong>の学習塾NICKでは、<strong>無料体験授業</strong>を随時受け付けております。<strong>定期テスト対策</strong>や<strong>高校受験対策</strong>について、詳しくご説明いたします。<strong>廿日市市</strong>、<strong>宮島</strong>、<strong>阿品</strong>、<strong>大竹市</strong>など周辺地域からもお気軽にお越しください。
            </p>

            <div className="bg-white/10 rounded-3xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
                <div>
                  <i className="ri-time-line text-4xl text-[#4a90e2] mb-4"></i>
                  <p className="font-bold mb-2">開校時間</p>
                  <p className="text-sm text-white/80">
                    平日 14:00〜22:00<br />
                    土曜 10:00〜18:00
                  </p>
                </div>
                <div>
                  <i className="ri-phone-line text-4xl text-[#4a90e2] mb-4"></i>
                  <p className="font-bold mb-2">お電話</p>
                  <p className="text-sm text-white/80">
                    0829-78-1638
                  </p>
                </div>
                <div>
                  <i className="ri-map-pin-line text-4xl text-[#4a90e2] mb-4"></i>
                  <p className="font-bold mb-2">所在地</p>
                  <p className="text-sm text-white/80">
                    廿日市市大野中央<br />
                    四丁目４－１６
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
